import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AdminMemberView,
  MemberDetails,
  PendingInvitation,
  MemberStats,
  InvitationStats,
  MembershipRoles,
  InvitationEmailStatus,
} from '../models/membership.model';

/**
 * API response for members list
 */
interface MembersListResponse {
  success: boolean;
  data: {
    members: AdminMemberView[];
    stats: MemberStats;
  };
  error?: string;
}

/**
 * API response for invitations list
 */
interface InvitationsListResponse {
  success: boolean;
  data: {
    invitations: PendingInvitation[];
    stats: InvitationStats;
  };
  error?: string;
}

/**
 * API response for member details
 */
interface MemberDetailsResponse {
  success: boolean;
  data: MemberDetails;
  error?: string;
}

/**
 * API response for generic operations
 */
interface GenericResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * API response for send-email operation
 */
interface SendEmailResponse {
  success: boolean;
  message?: string;
  data?: {
    emailStatus: InvitationEmailStatus;
    emailSentAt: string;
    resendCount: number;
  };
  error?: string;
}

/**
 * Create invitation request
 */
interface CreateInvitationRequest {
  email: string;
  firstName?: string;
  lastName?: string;
  role: number;
}

/**
 * Update role request
 */
interface UpdateRoleRequest {
  role: number;
}

/**
 * Transfer ownership request
 */
interface TransferOwnershipRequest {
  ownerProfileId: string;
  oldOwnerRole?: number;
}

/**
 * Assign chief supervisor request
 */
interface AssignChiefSupervisorRequest {
  chiefSupervisorProfileId: string | null;
}

/**
 * Membership Service
 *
 * Handles member and invitation management for admin portal.
 */
@Injectable({
  providedIn: 'root',
})
export class MembershipService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get organization members (Admin)
   * GET /admin/organizations/:organizationId/memberships
   *
   * @param organizationId Organization ID
   * @param status Filter by status: 'active' | 'inactive' | 'all'
   * @returns Members list with stats
   */
  async getOrganizationMembers(
    organizationId: string,
    status: 'active' | 'inactive' | 'all' = 'active'
  ): Promise<{ members: AdminMemberView[]; stats: MemberStats }> {
    let params = new HttpParams();
    if (status !== 'all') {
      params = params.set('status', status);
    }

    const response = await firstValueFrom(
      this.http.get<MembersListResponse>(
        `${this.apiUrl}/admin/organizations/${organizationId}/memberships`,
        { params }
      )
    );

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch members');
    }

    return response.data;
  }

  /**
   * Get member details (full profile)
   * GET /admin/profiles/:profileId
   *
   * Note: Uses admin endpoint for full profile access
   *
   * @param profileId Profile ID
   * @returns Full member details
   */
  async getMemberDetails(profileId: string): Promise<MemberDetails> {
    const response = await firstValueFrom(
      this.http.get<MemberDetailsResponse>(
        `${this.apiUrl}/admin/profiles/${profileId}`
      )
    );

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch member details');
    }

    return response.data;
  }

  /**
   * Get pending invitations for organization (Admin)
   * GET /admin/organizations/:organizationId/invitations
   *
   * @param organizationId Organization ID
   * @returns Pending invitations list with stats
   */
  async getPendingInvitations(
    organizationId: string
  ): Promise<{ invitations: PendingInvitation[]; stats: InvitationStats }> {
    const params = new HttpParams().set('status', 'pending');

    const response = await firstValueFrom(
      this.http.get<InvitationsListResponse>(
        `${this.apiUrl}/admin/organizations/${organizationId}/invitations`,
        { params }
      )
    );

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch invitations');
    }

    return response.data;
  }

  /**
   * Invite member to organization (Admin)
   * POST /admin/organizations/:organizationId/invitations
   *
   * @param organizationId Organization ID
   * @param data Invitation data
   * @returns Success message
   */
  async inviteMember(
    organizationId: string,
    data: CreateInvitationRequest
  ): Promise<{ message: string }> {
    const response = await firstValueFrom(
      this.http.post<GenericResponse>(
        `${this.apiUrl}/admin/organizations/${organizationId}/invitations`,
        data
      )
    );

    if (!response.success) {
      throw new Error(response.error || 'Failed to invite member');
    }

    return { message: response.message || 'Invitation sent successfully' };
  }

  /**
   * Resend invitation (Admin)
   * POST /admin/organizations/:organizationId/invitations/:invitationId/resend
   *
   * @param organizationId Organization ID
   * @param invitationId Invitation ID
   * @returns Success message
   */
  async resendInvitation(
    organizationId: string,
    invitationId: string
  ): Promise<{ message: string }> {
    const response = await firstValueFrom(
      this.http.post<GenericResponse>(
        `${this.apiUrl}/admin/organizations/${organizationId}/invitations/${invitationId}/resend`,
        {}
      )
    );

    if (!response.success) {
      throw new Error(response.error || 'Failed to resend invitation');
    }

    return { message: response.message || 'Invitation resent successfully' };
  }

  /**
   * Cancel invitation (Admin)
   * DELETE /admin/organizations/:organizationId/invitations/:invitationId
   *
   * @param organizationId Organization ID
   * @param invitationId Invitation ID
   * @returns Success message
   */
  async cancelInvitation(
    organizationId: string,
    invitationId: string
  ): Promise<{ message: string }> {
    const response = await firstValueFrom(
      this.http.delete<GenericResponse>(
        `${this.apiUrl}/admin/organizations/${organizationId}/invitations/${invitationId}`
      )
    );

    if (!response.success) {
      throw new Error(response.error || 'Failed to cancel invitation');
    }

    return { message: response.message || 'Invitation cancelled successfully' };
  }

  /**
   * Update member role (Admin)
   * PUT /admin/organizations/:organizationId/memberships/:profileId
   *
   * This updates the membership.role field with Pilot (1) and/or PIC (2) bits.
   * Owner and Chief Supervisor are organization-level fields, not membership roles.
   *
   * @param organizationId Organization ID
   * @param profileId Profile ID
   * @param role New role (bitwise: 0-3)
   * @returns Success message
   */
  async updateMemberRole(
    organizationId: string,
    profileId: string,
    role: number
  ): Promise<{ message: string }> {
    const data: UpdateRoleRequest = { role };

    const response = await firstValueFrom(
      this.http.put<GenericResponse>(
        `${this.apiUrl}/admin/organizations/${organizationId}/memberships/${profileId}`,
        data
      )
    );

    if (!response.success) {
      throw new Error(response.error || 'Failed to update member role');
    }

    return { message: response.message || 'Member role updated successfully' };
  }

  /**
   * Transfer organization ownership
   * PUT /admin/organizations/:organizationId
   *
   * Updates organization.owner_profile_id to new owner.
   * Optionally assigns old owner a new membership role.
   *
   * @param organizationId Organization ID
   * @param newOwnerProfileId Profile ID of new owner
   * @param oldOwnerRole Optional role for old owner (0-3, or undefined to keep current)
   * @returns Success message
   */
  async transferOwnership(
    organizationId: string,
    newOwnerProfileId: string,
    oldOwnerRole?: number
  ): Promise<{ message: string }> {
    const data: TransferOwnershipRequest = {
      ownerProfileId: newOwnerProfileId
    };

    // Include old owner role if specified
    if (oldOwnerRole !== undefined) {
      data.oldOwnerRole = oldOwnerRole;
    }

    const response = await firstValueFrom(
      this.http.put<GenericResponse>(
        `${this.apiUrl}/admin/organizations/${organizationId}`,
        data
      )
    );

    if (!response.success) {
      throw new Error(response.error || 'Failed to transfer ownership');
    }

    return { message: response.message || 'Ownership transferred successfully' };
  }

  /**
   * Assign or remove Chief Supervisor
   * PUT /admin/organizations/:organizationId
   *
   * Updates organization.chief_supervisor_profile_id.
   * Only one CS allowed per organization.
   *
   * @param organizationId Organization ID
   * @param profileId Profile ID of new CS (or null to remove)
   * @returns Success message
   */
  async assignChiefSupervisor(
    organizationId: string,
    profileId: string | null
  ): Promise<{ message: string }> {
    const data: AssignChiefSupervisorRequest = {
      chiefSupervisorProfileId: profileId
    };

    const response = await firstValueFrom(
      this.http.put<GenericResponse>(
        `${this.apiUrl}/admin/organizations/${organizationId}`,
        data
      )
    );

    if (!response.success) {
      throw new Error(response.error || 'Failed to assign chief supervisor');
    }

    return { message: response.message || 'Chief supervisor assigned successfully' };
  }

  /**
   * Remove member from organization (Admin)
   * DELETE /admin/organizations/:organizationId/memberships/:profileId
   *
   * @param organizationId Organization ID
   * @param profileId Profile ID
   * @returns Success message
   */
  async removeMember(
    organizationId: string,
    profileId: string
  ): Promise<{ message: string }> {
    const response = await firstValueFrom(
      this.http.delete<GenericResponse>(
        `${this.apiUrl}/admin/organizations/${organizationId}/memberships/${profileId}`
      )
    );

    if (!response.success) {
      throw new Error(response.error || 'Failed to remove member');
    }

    return { message: response.message || 'Member removed successfully' };
  }

  /**
   * Reactivate member (Admin)
   * POST /admin/organizations/:organizationId/memberships/:profileId/reactivate
   *
   * @param organizationId Organization ID
   * @param profileId Profile ID
   * @returns Success message
   */
  async reactivateMember(
    organizationId: string,
    profileId: string
  ): Promise<{ message: string }> {
    const response = await firstValueFrom(
      this.http.post<GenericResponse>(
        `${this.apiUrl}/admin/organizations/${organizationId}/memberships/${profileId}/reactivate`,
        {}
      )
    );

    if (!response.success) {
      throw new Error(response.error || 'Failed to reactivate member');
    }

    return { message: response.message || 'Member reactivated successfully' };
  }

  /**
   * Calculate bitwise role from checkboxes
   *
   * @param isPilot Pilot checkbox state
   * @param isPIC PIC checkbox state
   * @returns Bitwise role value (0-3)
   */
  static calculateBitwiseRole(isPilot: boolean, isPIC: boolean): number {
    let role = 0;
    if (isPilot) {
      role |= MembershipRoles.Pilot;
    }
    if (isPIC) {
      role |= MembershipRoles.PilotInCharge;
    }
    return role;
  }

  /**
   * Validate role change
   * Ensures role is within bounds (0-3 for Pilot/PIC only)
   *
   * @param currentRole Current role value
   * @param newRole New role value
   * @returns Validation result with error message if invalid
   */
  static validateRoleChange(currentRole: number, newRole: number): { valid: boolean; error?: string } {
    // Validate role is within bounds (0-3 for Pilot/PIC only)
    if (newRole < 0 || newRole > 3) {
      return {
        valid: false,
        error: 'Invalid role value. Must be between 0 and 3.'
      };
    }

    // Backend will validate at least one PIC exists
    return { valid: true };
  }

  /**
   * Check if user can change member role
   * In admin portal, admins can change any role
   *
   * @param isAdmin Whether current user is admin
   * @param targetMember Member being modified (optional)
   * @returns Whether role can be changed
   */
  static canChangeRole(isAdmin: boolean, targetMember?: any): boolean {
    return isAdmin;
  }

  /**
   * Format role value to display names
   *
   * @param role Bitwise role value
   * @returns Array of role names
   */
  static getRoleDisplayNames(role: number): string[] {
    const roles: string[] = [];
    if ((role & MembershipRoles.Pilot) === MembershipRoles.Pilot) {
      roles.push('Pilot');
    }
    if ((role & MembershipRoles.PilotInCharge) === MembershipRoles.PilotInCharge) {
      roles.push('Pilot in Command');
    }
    return roles;
  }

  /**
   * Check if role has specific bit
   *
   * @param role Bitwise role value
   * @param bit Bit to check
   * @returns Whether role has the bit
   */
  static hasRole(role: number, bit: MembershipRoles): boolean {
    return (role & bit) === bit;
  }

  /**
   * Send invitation email (Admin)
   * POST /admin/organization-invitations/:invitationId/send-email
   *
   * Triggers the invitation email for invitations that haven't been sent yet.
   * Returns updated email status data from the API response.
   *
   * @param invitationId Invitation ID
   * @returns Email status data including emailStatus, emailSentAt, and resendCount
   */
  async sendInvitationEmail(invitationId: string): Promise<{
    message: string;
    emailStatus: InvitationEmailStatus;
    emailSentAt: string;
    resendCount: number;
  }> {
    const response = await firstValueFrom(
      this.http.post<SendEmailResponse>(
        `${this.apiUrl}/admin/organization-invitations/${invitationId}/send-email`,
        {}
      )
    );

    if (!response.success) {
      throw new Error(response.error || 'Failed to send invitation email');
    }

    return {
      message: response.message || 'Invitation email sent successfully',
      emailStatus: response.data?.emailStatus ?? 'sent',
      emailSentAt: response.data?.emailSentAt ?? new Date().toISOString(),
      resendCount: response.data?.resendCount ?? 0,
    };
  }
}
